import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

import { ShiftInfoPanel } from "./ShiftInfoPanel";
import List from "../../../../../components/List";
import { ShiftCreateEdit } from "./ShiftCreateEdit";
import { useShifts } from "../../../../../hooks/query/useShifts";
import { IShift } from "../../../../../interfaces/ShiftInterface";
import { formatTime12hr } from "../../../../../helpers/formatters";
import { EmptyView } from "../../../../../components/OList";

// eslint-disable-next-line import/prefer-default-export
export function ShiftsList(): React.ReactNode {
  const { data: shifts = [], isLoading } = useShifts();

  const [showCreateEditPanel, setShowCreateEditPanel] = useState(false);
  const [selectedShift, setSelectedShift] = useState<IShift | null>(null);

  const handleSuccess = () => {
    // to close the panel after edit or creation, set to null
    setSelectedShift(null);
    setShowCreateEditPanel(false);
  };

  return (
    <Box height="100%">
      <List<IShift>
        automation="shift-list"
        data={shifts}
        isLoading={isLoading}
        rowHeight={55}
        onCreateNew={() => setShowCreateEditPanel(true)}
        createLabel="Create Shift"
        isResponsive
        activeRow={selectedShift?.id}
        withoutSearch
        onRowClick={(row) => {
          setSelectedShift(selectedShift?.id === row.id ? null : (row as IShift));
        }}
        columns={[
          {
            key: "name",
            label: "Shift Name",
            sortable: true,
            formatter: (row: IShift) => (
              <Typography variant="body2" fontWeight={500}>
                {row.name}
              </Typography>
            )
          },
          {
            key: "shiftCode",
            label: "Code",
            sortable: true,
            hideInNarrowView: true,
            formatter: (row: IShift) => <Typography variant="body2">{row.shiftCode}</Typography>
          },
          {
            key: "startTime",
            label: "Start Time",
            formatter: (row: IShift) => (
              <Typography variant="body2">{formatTime12hr(row.startTime)}</Typography>
            )
          },
          {
            key: "endTime",
            label: "End Time",
            formatter: (row: IShift) => (
              <Typography variant="body2">{formatTime12hr(row.endTime)}</Typography>
            )
          },
          {
            key: "breakStartTime",
            label: "Break Start",
            hideInNarrowView: true,
            formatter: (row: IShift) => (
              <Typography variant="body2">{formatTime12hr(row.breakStartTime)}</Typography>
            )
          },
          {
            key: "breakEndTime",
            label: "Break End",
            hideInNarrowView: true,
            formatter: (row: IShift) => (
              <Typography variant="body2">{formatTime12hr(row.breakEndTime)}</Typography>
            )
          },
          {
            key: "graceInMinutes",
            label: "Grace In",
            hideInNarrowView: true,
            formatter: (row: IShift) => (
              <Typography variant="body2">{row.graceInMinutes || 0}m</Typography>
            )
          },
          {
            key: "graceOutMinutes",
            label: "Grace Out",
            hideInNarrowView: true,
            formatter: (row: IShift) => (
              <Typography variant="body2">{row.graceOutMinutes || 0}m</Typography>
            )
          },
          {
            key: "status",
            label: "Status",
            formatter: (row: IShift) => (
              <Typography
                variant="body2"
                sx={{
                  color: row.status === "active" ? "success.main" : "error.main",
                  textTransform: "capitalize"
                }}
              >
                {row.status}
              </Typography>
            )
          }
        ]}
      >
        <EmptyView>
          <Typography component="div" variant="body2">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "350px",
                width: "100%"
              }}
            >
              <Typography component="div">
                <Box textAlign="center" marginTop="16px">
                  No Shift records found
                </Box>
              </Typography>
            </Box>
          </Typography>
        </EmptyView>
      </List>

      {/* panels */}
      {selectedShift && (
        <ShiftInfoPanel
          shift={selectedShift}
          onClose={() => setSelectedShift(null)}
          onEdit={() => setShowCreateEditPanel(true)}
        />
      )}

      {showCreateEditPanel && (
        <ShiftCreateEdit
          onClose={() => {
            setShowCreateEditPanel(false);
            setSelectedShift(null);
          }}
          shift={selectedShift}
          onSuccess={handleSuccess}
        />
      )}
    </Box>
  );
}
